/**
 * Карточка проекта (US-26 FR-4) — модальное окно поверх затемнения.
 *
 * Открывается кликом по строке реестра и по проекту виджета «Крупнейшие
 * проекты» — одной и той же операцией `/public/projects/card/{uid}`
 * (FR-4.1, FR-7.2); закрывается кнопкой, Esc и кликом по затемнению
 * (поведение примитива `Dialog`). Тумблером видимости карточка не
 * гасится: она открывается из двух мест, и привязка к одному тумблеру
 * оборвала бы второй путь (US-27, DP-446).
 *
 * Состав: шапка «название + территория · вид работ · АБП» (FR-4.2), блок
 * «Общая информация» (FR-4.3) и таблица «Финансирование» со строками
 * «Общая» / «СМР» / «Надзор» / «Прочее» (FR-4.4).
 *
 * Строка **«Общая» приходит из выдачи готовой** (`money` карточки) и
 * сложением договоров не собирается (FR-4.5). Строки «СМР», «Надзор» и
 * «Прочее» — свёртка договоров проекта по видам: FR-4.4 прямо требует
 * сворачивать все договоры надзора в одну строку, а разбивку по
 * техническому и авторскому надзору показывает раскрытие строки реестра.
 * Проект без договора надзора строки «Надзор» не имеет, и «Общая» тогда
 * равна «СМР» — это нормальное состояние, а не потеря данных.
 *
 * Карточка открывается и при неполных реквизитах: неизвестное поле
 * показывает отсутствие значения и открытие не блокирует (FR-4.6).
 *
 * Drill-through неактивен: без первичных данных — по FR-4.7, с ними —
 * пока не определён состав перехода (US-27, открытый вопрос 4).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { moneyMln, monthYear } from './format';
import { ProjectTypePill, StatusPill } from './StatusPill';
import { DevelopmentBar, NoData, useLocalizedText } from './projectsView';
import { BlockLoader } from '../../../components/common/BlockLoader';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import type {
  ProjectCard,
  ProjectContract,
  ProjectMoney,
} from '../../../types/projects';

/**
 * Сумма, в которой пустое считается нулём, но «всё пусто» остаётся
 * отсутствием значения. Ровно то же приведение, что делает выдача: если
 * пустое просто выпадает из суммы, получается правдоподобное и неверное
 * число, а сумма из одних пустых — не нуль (US-27, `_sum_or_none`).
 */
const sumOrNull = (values: Array<number | null>): number | null => {
  const present = values.filter((v): v is number => v !== null);
  if (present.length === 0) return null;
  return present.reduce((acc, v) => acc + v, 0);
};

/** Процент или отсутствие значения; нулевой знаменатель → `null`. */
const pctOrNull = (
  numerator: number | null,
  denominator: number | null,
): number | null => {
  if (numerator === null || denominator === null || denominator === 0) {
    return null;
  }
  return (numerator / denominator) * 100;
};

/** Свёртка договоров одного вида в строку таблицы финансирования. */
const foldMoney = (contracts: ProjectContract[]): ProjectMoney => {
  const pick = (field: keyof ProjectMoney): number | null =>
    sumOrNull(contracts.map((c) => c.money[field]));
  const totalCost = pick('totalCost');
  const spentTotal = pick('spentTotal');
  const planYear = pick('planYear');
  const spentInYear = pick('spentInYear');
  return {
    totalCost,
    spentBeforeYear: pick('spentBeforeYear'),
    planYear,
    spentInYear,
    spentTotal,
    remainder: pick('remainder'),
    executionPct: pctOrNull(spentTotal, totalCost),
    annualPlanPct: pctOrNull(spentInYear, planYear),
  };
};

interface FactProps {
  label: string;
  children: ReactNode;
  className?: string;
}

const Fact = ({ label, children, className }: FactProps) => (
  <div className={className}>
    <p className="text-[11px] uppercase tracking-wide text-text-faint">
      {label}
    </p>
    <div className="mt-0.5 text-sm font-semibold">{children}</div>
  </div>
);

/** Денежная ячейка таблицы финансирования: млн ₸ подписаны в заголовке. */
const Money = ({ value }: { value: number | null }) =>
  value === null ? <NoData /> : <>{moneyMln(value)}</>;

interface FinancingRowProps {
  label: ReactNode;
  money: ProjectMoney;
  bold?: boolean;
  testId: string;
}

const FinancingRow = ({ label, money, bold, testId }: FinancingRowProps) => (
  <TableRow
    className={cn(bold && 'bg-surface-muted/60 font-semibold')}
    data-testid={testId}
  >
    <TableCell className="whitespace-nowrap">{label}</TableCell>
    <TableCell className="text-right tabular-nums">
      <Money value={money.totalCost} />
    </TableCell>
    <TableCell className="text-right tabular-nums">
      <Money value={money.spentBeforeYear} />
    </TableCell>
    <TableCell className="text-right tabular-nums">
      <Money value={money.planYear} />
    </TableCell>
    <TableCell className="text-right tabular-nums">
      <Money value={money.spentInYear} />
    </TableCell>
    <TableCell className="text-right tabular-nums">
      <Money value={money.remainder} />
    </TableCell>
    <TableCell>
      <DevelopmentBar pct={money.executionPct} />
    </TableCell>
  </TableRow>
);

interface ProjectCardDialogProps {
  /** `null` при закрытой карточке и пока идёт запрос по `open`. */
  card: ProjectCard | null;
  /** Карточка запрошена: окно открыто, содержимое грузится. */
  open: boolean;
  loading: boolean;
  onClose: () => void;
}

export const ProjectCardDialog = ({
  card,
  open,
  loading,
  onClose,
}: ProjectCardDialogProps) => {
  const { t } = useTranslation();
  const loc = useLocalizedText();

  if (!open) return null;

  const year = card?.reportYear ?? null;
  const workKindLabel =
    card?.workKind == null
      ? null
      : t(`projects.registry.workKinds.${card.workKind}`, {
          defaultValue: card.workKind,
        });
  const subtitle = [
    loc(card?.katoName ?? null),
    workKindLabel,
    loc(card?.abpName ?? null),
  ]
    .filter(Boolean)
    .join(' · ');

  const start = monthYear(card?.startDate ?? null);
  const end = monthYear(card?.endDate ?? null);
  const period =
    start === null && end === null ? null : `${start ?? '…'} — ${end ?? '…'}`;

  // FR-4.3: бюджетная классификация — одной строкой; отсутствующая часть
  // не показывается, а не превращается в пустой разделитель.
  const budget =
    card === null
      ? null
      : [
          card.abpCode === null
            ? null
            : t('projects.card.budgetAbp', { value: card.abpCode }),
          card.budgetProgram === null
            ? null
            : t('projects.card.budgetProgram', { value: card.budgetProgram }),
          card.budgetSubprogram === null
            ? null
            : t('projects.card.budgetSubprogram', {
                value: card.budgetSubprogram,
              }),
          card.budgetSpecific === null
            ? null
            : t('projects.card.budgetSpecific', { value: card.budgetSpecific }),
        ]
          .filter(Boolean)
          .join(' · ') || null;

  const contracts = card?.contracts ?? [];
  const smr = contracts.filter((c) => c.kind === 'SMR');
  const supervision = contracts.filter((c) => c.kind === 'SUPERVISION');
  const other = contracts.filter(
    (c) => c.kind !== 'SMR' && c.kind !== 'SUPERVISION',
  );
  const otherNote = [...new Set(other.map((c) => c.kind))]
    .map((kind) =>
      t(`projects.registry.contractKinds.${kind}`, { defaultValue: kind }),
    )
    .join(', ');

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="max-h-[90vh] w-[calc(100%-2rem)] max-w-4xl overflow-y-auto"
        data-testid="projects-card"
      >
        <DialogHeader>
          <DialogTitle className="pr-6 text-left leading-snug">
            {loc(card?.name ?? null) ?? t('projects.card.title')}
          </DialogTitle>
          <DialogDescription className="text-left">
            {subtitle}
          </DialogDescription>
        </DialogHeader>

        {loading || card === null ? (
          <BlockLoader testId="projects-card-loader" />
        ) : (
          <>
            <section data-testid="projects-card-general">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-faint">
                {t('projects.card.general')}
              </h4>
              <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                <Fact label={t('projects.card.period')}>
                  {period === null ? (
                    <NoData />
                  ) : (
                    <span className="tabular-nums">{period}</span>
                  )}
                </Fact>
                <Fact label={t('projects.card.status')}>
                  <span className="flex flex-wrap items-center gap-2">
                    <ProjectTypePill type={card.projectType} />
                    {card.status === null ? (
                      <NoData />
                    ) : (
                      <StatusPill status={card.status} />
                    )}
                  </span>
                </Fact>
                <Fact label={t('projects.card.customer')}>
                  {loc(card.abpName) ?? <NoData />}
                </Fact>
                <Fact label={t('projects.card.contractor')}>
                  {card.contractorName ?? <NoData />}
                </Fact>
                <Fact
                  label={t('projects.card.budget')}
                  className="sm:col-span-2"
                >
                  {budget ?? <NoData />}
                </Fact>
              </div>
            </section>

            <section data-testid="projects-card-financing">
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-faint">
                {t('projects.card.financing')}
                <span className="font-normal normal-case tracking-normal text-text-muted">
                  {' · '}
                  {t('projects.units.mlnTg')}
                </span>
              </h4>
              <div className="overflow-x-auto">
                <Table className="min-w-[46rem]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('projects.card.fin.kind')}</TableHead>
                      <TableHead className="whitespace-nowrap text-right">
                        {t('projects.card.fin.totalCost')}
                      </TableHead>
                      <TableHead className="whitespace-nowrap text-right">
                        {t('projects.card.fin.spentBefore', {
                          year: year ?? '',
                        })}
                      </TableHead>
                      <TableHead className="whitespace-nowrap text-right">
                        {t('projects.card.fin.planYear', { year: year ?? '' })}
                      </TableHead>
                      <TableHead className="whitespace-nowrap text-right">
                        {t('projects.card.fin.spentInYear', {
                          year: year ?? '',
                        })}
                      </TableHead>
                      <TableHead className="whitespace-nowrap text-right">
                        {t('projects.card.fin.remainder')}
                      </TableHead>
                      <TableHead className="text-right">
                        {t('projects.card.fin.development')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <FinancingRow
                      label={t('projects.card.fin.rowTotal')}
                      money={card.money}
                      bold
                      testId="projects-card-fin-total"
                    />
                    {smr.length > 0 && (
                      <FinancingRow
                        label={t('projects.card.fin.rowSmr')}
                        money={foldMoney(smr)}
                        testId="projects-card-fin-smr"
                      />
                    )}
                    {supervision.length > 0 && (
                      <FinancingRow
                        label={t('projects.card.fin.rowSupervision')}
                        money={foldMoney(supervision)}
                        testId="projects-card-fin-supervision"
                      />
                    )}
                    {other.length > 0 && (
                      <FinancingRow
                        label={
                          <>
                            {t('projects.card.fin.rowOther')}
                            <span className="ml-1 text-xs font-normal text-text-muted">
                              ({otherNote})
                            </span>
                          </>
                        }
                        money={foldMoney(other)}
                        testId="projects-card-fin-other"
                      />
                    )}
                  </TableBody>
                </Table>
              </div>
            </section>

            <div className="flex flex-wrap items-center gap-3">
              <Button disabled data-testid="projects-card-drill">
                {t('projects.card.drill')}
              </Button>
              <span className="text-xs text-text-faint">
                {card.hasPrimaryData
                  ? t('projects.card.drillPending')
                  : t('projects.card.drillNoData')}
              </span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
